// Griff:
// These are the webcomponents to support drag and drop in Elm nicely.
//
// A few things of note!
// 1. we do not touch any of the rendered Html that is under the control of the Elm app.
// 2. We do render the moving item, but we do this outside of the Elm app, at the top of the DOM.
// 3. The webcomponents mostly are here to fire events back to Elm-land once interesting stuff has happened.
// 4. We disable text selection globally when a drag is occurring
//
// Note, we are skipping the Html5 implementation of drag and drop because Chrome disallows setting the cursor when dragging something
// https://stackoverflow.com/questions/44447210/custom-cursor-with-drag-and-drop-an-html-element-without-libraries
// What a weird limitation.

// -------- Some app-wide global state.

type AutoScrollElement = {
  previousTime: DOMHighResTimeStamp | null
  speed: number
  element: HTMLElement
  up: boolean
  size: {
    width: number
    height: number
  }
}

let autoScrollElement: AutoScrollElement | null = null

// if we are dragging an item, then it will be stored in draggingItem
type DraggingItem = {
  elem: HTMLElement
  source: Element
  offset: { x: number; y: number }
  over: string | null
  viewports: ViewportRect[]
  zones: DropZone[] | null
  autoscrollYOffset: number
  userScrollXParent: Element | null
  userScrollYParent: Element | null
  startingUserScrollXOffset: number
  startingUserScrollYOffset: number
  dragVertical: boolean
}

let draggingItem: DraggingItem | null = null

// Some constants
const scrollTriggerDistance = 150

// --------  Events that will be communicated to the Elm app!
// Note, all of thes are dispatched through the *element that is being dragged*
const toElmDragStart = () => {
  return new CustomEvent("vendr-drag-start", { detail: {} })
}

// dropzoneId can be null | string
const toElmDragOver = (dropzoneId: string): CustomEvent => {
  return new CustomEvent("vendr-drag-over", { detail: { dropzoneId } })
}

// dropzoneId can be null | string
const toElmDrop = (dropzoneId?: string | null): CustomEvent => {
  return new CustomEvent("vendr-drop", { detail: { dropzoneId } })
}

// These are known classes that are used to coordinate with WebComponent/DragAndDrop.elm
const knownClasses = {
  scrollable: "vendr-drag-scrollable",
  draggable: "vendr-drag-draggable",
  zone: "vendr-dropzone",
}

// --------  Event handlers on the js side of things

// If there is an element to autoscroll, this function will scroll it.
const autoscroll = (timestamp: DOMHighResTimeStamp) => {
  if (autoScrollElement) {
    let keepScrolling = false
    if (autoScrollElement.previousTime === null) {
      autoScrollElement.previousTime = timestamp
      keepScrolling = true
    } else {
      const deltaMs = timestamp - autoScrollElement.previousTime
      // 100 pixels / second = 0.1 pixel /ms
      // So this is 1000 pixels/second
      const speed = 0.8 * autoScrollElement.speed

      const scrollRate = deltaMs * speed
      let scrollDelta = 0

      if (autoScrollElement.up) {
        scrollDelta = Math.min(autoScrollElement.element.scrollTop, scrollRate) * -1
      } else {
        // const maxScrollTop = autoScrollElement.element.scrollHeight - autoScrollElement.size.height
        const distanceToBottom =
          autoScrollElement.element.scrollHeight - (autoScrollElement.element.scrollTop + autoScrollElement.size.height)
        scrollDelta = Math.min(distanceToBottom, scrollRate)
      }
      if (scrollDelta !== 0) {
        keepScrolling = true
      }

      if (draggingItem) {
        draggingItem.autoscrollYOffset = draggingItem.autoscrollYOffset + scrollDelta
      }
      autoScrollElement.element.scrollTop = autoScrollElement.element.scrollTop + scrollDelta
      autoScrollElement.previousTime = timestamp
    }
    if (keepScrolling) {
      window.requestAnimationFrame(autoscroll)
    }
  }
}

const progressBetween = (num: number, top: number, bottom: number): number => {
  const normalized = Math.min(Math.max(num, bottom), top)
  return (normalized - bottom) / (top - bottom)
}

// Given mouseX, mouseY, and a potentially scrollable element
// Check if we should autoscroll the scrollable element.
// return either null or the state that should be stored in autoScrollElement
const shouldAutoScroll = (mouseY: number, scrollable: ViewportRect): boolean => {
  if (scrollable.element.scrollTop > 0 && mouseY < scrollable.rect.y + scrollTriggerDistance) {
    const speed = 1 - progressBetween(mouseY, scrollable.rect.y + scrollTriggerDistance, scrollable.rect.y)

    // Scroll up
    if (autoScrollElement) {
      autoScrollElement.speed = speed
      return true
    } else {
      autoScrollElement = {
        element: scrollable.element,
        up: true,
        size: { width: scrollable.rect.width, height: scrollable.rect.height },
        speed: speed,
        previousTime: null,
      }
      return true
    }
  }

  // Previously we had added 100px here to make it so that the container would scroll when the guestimated bottom
  // of the dragged element was close to the bottom of the container.
  // However, that's probably not necessary
  const bottomOfdraggedElement = mouseY
  const bottomY = scrollable.rect.y + scrollable.rect.height

  const distanceFromBottom = scrollable.element.scrollHeight - (scrollable.element.scrollTop + scrollable.rect.height)

  const scrollDownStartPoint = bottomY - scrollTriggerDistance

  if (distanceFromBottom > 0 && bottomOfdraggedElement > scrollDownStartPoint) {
    // Scroll Down

    const speed = progressBetween(bottomOfdraggedElement, bottomY, scrollDownStartPoint)

    if (autoScrollElement) {
      autoScrollElement.speed = speed
      return true
    } else {
      autoScrollElement = {
        element: scrollable.element,
        up: false,
        size: { width: scrollable.rect.width, height: scrollable.rect.height },
        speed: speed,
        previousTime: null,
      }
      return true
    }
  }

  return false
}

const clearDragArea = () => {
  const draggingArea = document.getElementById("toplevel-dragging-area")
  let child = draggingArea?.lastElementChild
  while (child) {
    draggingArea?.removeChild(child)
    child = draggingArea?.lastElementChild
  }
}

const onDragStart = (mouseEvent: MouseEvent, elem: HTMLElement, dragVertical: boolean) => {
  const rect = elem.getBoundingClientRect()
  const dragging = elem.cloneNode(true) as HTMLElement

  const moving = document.createElement("div")

  const x = mouseEvent.clientX
  const y = mouseEvent.clientY

  const offsetX = x - rect.x
  const offsetY = y - rect.y

  // This helps with `Ui.width.fill` elements whose width depends on their parent.
  if (dragVertical) {
    dragging.style.minWidth = `${rect.width}px`
  } else {
    dragging.style.minHeight = `${rect.height}px`
  }

  // This lets us conditionally hide content from the dragged "ghost" by sprinkling
  // the [data-hide-when-moving] attribute on the Elm side.
  for (const child of dragging.querySelectorAll("[data-hide-when-moving]")) {
    child.parentElement?.removeChild(child)
  }

  moving.classList.add("vendr-item-being-dragged")
  moving.style.position = "fixed"
  moving.style.transform = `translate(${x - offsetX}px, ${y - offsetY}px)`
  moving.style.zIndex = "2"
  moving.style.left = "0"
  moving.style.top = "0"
  moving.style.pointerEvents = "none"
  moving.appendChild(dragging)

  const userScrollXParent = getScrollXParent(elem)
  const userScrollYParent = getScrollYParent(elem)

  draggingItem = {
    elem: moving,
    source: elem,
    offset: { x: offsetX, y: offsetY },
    over: null,
    viewports: getViewportRects(),
    zones: null,
    autoscrollYOffset: 0,
    userScrollXParent,
    userScrollYParent,
    startingUserScrollXOffset: userScrollXParent?.scrollLeft || 0,
    startingUserScrollYOffset: userScrollYParent?.scrollTop || 0,
    dragVertical: dragVertical,
  }

  clearDragArea()
  const draggingArea = document.getElementById("toplevel-dragging-area")

  draggingArea?.appendChild(moving)
  window.addEventListener("mousemove", onMove)
  window.addEventListener("mouseup", onStop)
  document.body.classList.add("global-dragging")
}

const getZoneAtCoords = (vertical: boolean, zones: DropZone[], x: number, y: number): string | null => {
  if (vertical) {
    // This is the current y coordinate, but offset if there has been any autoscrolling
    const currentUserScrollYOffset = draggingItem?.userScrollYParent?.scrollTop || 0
    const userScrollYOffsetDelta = currentUserScrollYOffset - (draggingItem?.startingUserScrollYOffset || 0)
    const mouseY = y + (draggingItem?.autoscrollYOffset || 0) + userScrollYOffsetDelta
    for (const zone of zones) {
      if (mouseY > zone.rect.y && mouseY < zone.rect.y + zone.rect.height) {
        if (zone.zoneId !== draggingItem?.over) {
          return zone.zoneId
        }
      }
    }
    return null
  } else {
    // Dragging horizontally
    const currentUserScrollXOffset = draggingItem?.userScrollXParent?.scrollLeft || 0
    const userScrollXOffsetDelta = currentUserScrollXOffset - (draggingItem?.startingUserScrollXOffset || 0)
    const mouseX = x + userScrollXOffsetDelta
    for (const zone of zones) {
      if (mouseX > zone.rect.x && mouseX < zone.rect.x + zone.rect.width) {
        if (zone.zoneId !== draggingItem?.over) {
          return zone.zoneId
        }
      }
    }
    return null
  }
}

// https://stackoverflow.com/questions/35939886/find-first-scrollable-parent
const getScrollYParent = (node: Element | null): Element | null => {
  const isElement = node instanceof HTMLElement
  const overflowY = isElement && window.getComputedStyle(node).overflowY
  const isScrollable = overflowY !== "visible" && overflowY !== "hidden"

  if (!node) {
    return null
  } else if (isScrollable && node.scrollHeight >= node.clientHeight) {
    return node
  }

  return getScrollYParent(node?.parentNode as Element) || document.body
}
const getScrollXParent = (node: Element | null): Element | null => {
  const isElement = node instanceof HTMLElement
  const overflowX = isElement && window.getComputedStyle(node).overflowX
  const isScrollable = overflowX !== "visible" && overflowX !== "hidden"

  if (!node) {
    return null
  } else if (isScrollable && node.scrollWidth >= node.clientWidth) {
    return node
  }

  return getScrollXParent(node?.parentNode as Element) || document.body
}

const onMove = (event: MouseEvent) => {
  const x = event.clientX
  const y = event.clientY

  if (draggingItem) {
    if (draggingItem.zones === null) {
      draggingItem.zones = getDropZones()
      if (draggingItem.zones === null) {
        // We didn't find any zones yet, check again next time around.
        return
      }
    }

    draggingItem.elem.style.transform = `translate(${x - draggingItem.offset.x}px, ${y - draggingItem.offset.y}px)`

    let autoScrolling = false

    // Scroll an element if we're on the edge and it's scrollable
    for (const scrollableElement of draggingItem.viewports) {
      if (shouldAutoScroll(y, scrollableElement)) {
        autoScrolling = true
      }
    }
    if (autoScrolling) {
      // kick off the thing that actually does the scrolling
      window.requestAnimationFrame(autoscroll)
    } else {
      autoScrollElement = null
    }

    const zoneId = getZoneAtCoords(draggingItem.dragVertical, draggingItem.zones, x, y)

    if (zoneId) {
      draggingItem.source.dispatchEvent(toElmDragOver(zoneId))
      draggingItem.over = zoneId
    }
  }
}

const onPageVisibilityChange = () => {
  // if we've navigated to another page, stop the drag
  if (draggingItem && document.visibilityState === "hidden") {
    onStop()
  }
}

//
const onStop = (event?: MouseEvent) => {
  if (draggingItem) {
    if (event) {
      draggingItem.source.dispatchEvent(toElmDrop(draggingItem.over))
    } else {
      // no mouse event, so this was triggered by a browser visibility change or something
      // So, drop it on nothing
      draggingItem.source.dispatchEvent(toElmDrop())
    }
    window.removeEventListener("mousemove", onMove)
    window.removeEventListener("mouseup", onStop)
  }
  draggingItem = null
  autoScrollElement = null
  clearDragArea()
  document.body.classList.remove("global-dragging")
}

type ViewportRect = {
  element: HTMLElement
  rect: DOMRect
}

const getViewportRects = (): ViewportRect[] => {
  const dragViewports: ViewportRect[] = []
  const scrollable = document.getElementsByClassName(knownClasses.scrollable)
  if (scrollable) {
    for (const view of scrollable) {
      const rect = view.getBoundingClientRect()
      dragViewports.push({ element: view as HTMLElement, rect: rect })
    }
  }
  return dragViewports
}

type DropZone = {
  element: Element
  rect: DOMRect
  zoneId: string | null
}

const getDropZones = (): DropZone[] | null => {
  const zones: DropZone[] = []
  const zoneElements = document.getElementsByClassName(knownClasses.zone)
  for (const zone of zoneElements) {
    const rect = zone.getBoundingClientRect()
    zones.push({ element: zone, rect: rect, zoneId: zone.getAttribute("zoneId") })
  }

  if (zones.length === 0) {
    return null
  } else {
    return zones
  }
}

// --------- Global Event Bindings!

document.addEventListener("visibilitychange", onPageVisibilityChange)

//  Handle
if (!customElements.get("vendr-drag-handle")) {
  customElements.define(
    "vendr-drag-handle",
    class extends HTMLElement {
      constructor() {
        super()
      }

      dragVertical: boolean | null = null

      connectedCallback() {
        const elem = this // eslint-disable-line @typescript-eslint/no-this-alias

        elem.addEventListener("mousedown", (event) => {
          const dragDirectionVertical = elem.dragVertical ? elem.dragVertical : false
          const draggable = elem.closest<HTMLElement>("." + knownClasses.draggable)
          if (draggable) {
            draggable.dispatchEvent(toElmDragStart())
            onDragStart(event, draggable, dragDirectionVertical)
          }
        })
      }
    },
  )
}

// Avoid TS2306 "File x is not a module." in TypeScript when this module is
// imported for its side effect.
export {}
